import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Copy, Trash2, Eye, ArrowLeft, Save, X, Image, MapPin, Link2, ChevronDown, ChevronUp, Upload, MoveUp, MoveDown, RefreshCw, GripVertical, Building2, UtensilsCrossed, TreePine, ShoppingBag, FileText, BarChart3, Zap, Volume2 } from 'lucide-react';
import AdminNav from '../components/AdminNav';
import GuidedSetup from '../components/GuidedSetup';
import ImageUpload from '../components/ImageUpload';
import CardPositioner from '../components/CardPositioner';
import { useLanguage } from '../i18n/LanguageContext';
import { useAdminAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRANDS = [
  { value: 'yulontap', label: 'YULONTAP' },
  { value: 'tapelligence', label: 'Tapelligence' },
];

const DEFAULT_MODES = [
  { key: 'standard', label: 'Standard' },
  { key: 'review_shield', label: 'B-Heard' },
  { key: 'review_shield_gift', label: 'B-Heard + Gift' },
  { key: 'global', label: 'Global' },
];

const LANGS = ['en', 'fr', 'es'];
const LANG_LABELS = { en: 'EN', fr: 'FR', es: 'ES' };

// ── Scene Template Presets ──
const SCENE_TEMPLATES = [
  {
    key: 'hotel',
    icon: Building2,
    label: 'Hotel',
    sceneName: { en: 'Guest Room', fr: 'Chambre', es: 'Habitación' },
    cards: [
      { label: { en: 'Guest Welcome', fr: 'Bienvenue', es: 'Bienvenida' }, position_x: 22, position_y: 25 },
      { label: { en: 'Room Feedback', fr: 'Avis chambre', es: 'Opinión' }, position_x: 72, position_y: 25 },
      { label: { en: 'Wi-Fi Access', fr: 'Accès Wi-Fi', es: 'Acceso Wi-Fi' }, position_x: 22, position_y: 55 },
      { label: { en: 'Concierge', fr: 'Concierge', es: 'Conserjería' }, position_x: 72, position_y: 55 },
      { label: { en: 'Room Service', fr: 'Service en chambre', es: 'Servicio' }, position_x: 47, position_y: 82 },
    ],
  },
  {
    key: 'restaurant',
    icon: UtensilsCrossed,
    label: 'Restaurant',
    sceneName: { en: 'Dining Room', fr: 'Salle à manger', es: 'Comedor' },
    cards: [
      { label: { en: 'Table Card', fr: 'Carte de table', es: 'Tarjeta de mesa' }, position_x: 22, position_y: 22 },
      { label: { en: 'Menu', fr: 'Menu', es: 'Menú' }, position_x: 72, position_y: 22 },
      { label: { en: 'Reservations', fr: 'Réservations', es: 'Reservas' }, position_x: 22, position_y: 52 },
      { label: { en: 'Reviews', fr: 'Avis', es: 'Reseñas' }, position_x: 72, position_y: 52 },
      { label: { en: 'Special Offers', fr: 'Offres spéciales', es: 'Ofertas' }, position_x: 22, position_y: 82 },
      { label: { en: 'Directions', fr: 'Itinéraire', es: 'Direcciones' }, position_x: 72, position_y: 82 },
    ],
  },
  {
    key: 'golf',
    icon: TreePine,
    label: 'Golf',
    sceneName: { en: 'Pro Shop', fr: 'Pro Shop', es: 'Tienda Pro' },
    cards: [
      { label: { en: 'Pro Shop', fr: 'Pro Shop', es: 'Tienda Pro' }, position_x: 22, position_y: 25 },
      { label: { en: 'Tee Time', fr: 'Heure de départ', es: 'Hora de salida' }, position_x: 72, position_y: 25 },
      { label: { en: 'Course Guide', fr: 'Guide du parcours', es: 'Guía del campo' }, position_x: 22, position_y: 55 },
      { label: { en: 'Clubhouse', fr: 'Club house', es: 'Casa club' }, position_x: 72, position_y: 55 },
      { label: { en: 'Reviews', fr: 'Avis', es: 'Reseñas' }, position_x: 47, position_y: 82 },
    ],
  },
  {
    key: 'retail',
    icon: ShoppingBag,
    label: 'Retail',
    sceneName: { en: 'Store Floor', fr: 'Surface de vente', es: 'Piso de tienda' },
    cards: [
      { label: { en: 'Welcome', fr: 'Bienvenue', es: 'Bienvenida' }, position_x: 22, position_y: 25 },
      { label: { en: 'Product Info', fr: 'Info produit', es: 'Info producto' }, position_x: 72, position_y: 25 },
      { label: { en: 'Loyalty Program', fr: 'Programme fidélité', es: 'Programa fidelidad' }, position_x: 22, position_y: 55 },
      { label: { en: 'Reviews', fr: 'Avis', es: 'Reseñas' }, position_x: 72, position_y: 55 },
      { label: { en: 'Store Locator', fr: 'Trouver un magasin', es: 'Localizador' }, position_x: 47, position_y: 82 },
    ],
  },
];

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...options });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

// Normalize a value to { en, fr, es } object
const normI18n = (v) => {
  if (!v) return { en: '', fr: '', es: '' };
  if (typeof v === 'string') return { en: v, fr: '', es: '' };
  return { en: v.en || '', fr: v.fr || '', es: v.es || '' };
};

// Resolve i18n field to display string
const displayI18n = (v, lang = 'en') => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v[lang] || v.en || Object.values(v).find(x => x) || '';
};

// ── Language-aware Text Input ──────────────────
const LangInput = ({ value, onChange, placeholder, testId, className = '' }) => {
  const [activeLang, setActiveLang] = useState('en');
  const obj = normI18n(value);

  return (
    <div className={className}>
      <div className="flex gap-0.5 mb-1">
        {LANGS.map(l => (
          <button key={l} type="button" onClick={() => setActiveLang(l)}
            className={`px-2 py-0.5 text-[9px] font-bold rounded-md transition-colors ${
              activeLang === l ? 'bg-[#0077FF] text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-600'
            }${!obj[l] && l !== 'en' ? ' opacity-60' : ''}`}
            data-testid={testId ? `${testId}-lang-${l}` : undefined}>
            {LANG_LABELS[l]}
          </button>
        ))}
      </div>
      <input
        value={obj[activeLang] || ''}
        onChange={e => onChange({ ...obj, [activeLang]: e.target.value })}
        onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
        data-testid={testId ? `${testId}-${activeLang}` : undefined}
      />
    </div>
  );
};

// ── List View ──────────────────────────────────
// ── Reorder Modal ──────────────────────────────
const ReorderModal = ({ experiences, onClose, onSave, language }) => {
  const [items, setItems] = useState(() => [...experiences]);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };

  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    move(dragIdx, i);
    setDragIdx(i);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(items.map(e => e.id));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-800">Reorder Experiences</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 max-h-[60vh] overflow-y-auto">
          <p className="text-xs text-slate-400 mb-3">Drag or use arrows to reorder. This order is used in the Showroom gallery.</p>
          {items.map((exp, i) => (
            <div key={exp.id}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1.5 border transition-colors cursor-grab active:cursor-grabbing
                ${dragIdx === i ? 'border-[#0077FF]/30 bg-blue-50/50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
              data-testid={`reorder-item-${exp.id}`}>
              <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-700 flex-1 truncate">{displayI18n(exp.name, language)}</span>
              <div className="flex items-center gap-0.5">
                <button onClick={() => move(i, i - 1)} disabled={i === 0}
                  className="p-1 text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed rounded" data-testid={`reorder-up-${i}`}>
                  <MoveUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => move(i, i + 1)} disabled={i === items.length - 1}
                  className="p-1 text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed rounded" data-testid={`reorder-down-${i}`}>
                  <MoveDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
          <button onClick={onClose} className="px-3.5 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-lg">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
            data-testid="reorder-save-btn">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Order
          </button>
        </div>
      </div>
    </div>
  );
};

// Resolve a background URL — relative paths need the API prefix
const resolveThumbUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// Thumbnail with graceful broken-image handling
const ExperienceThumbnail = ({ exp, language }) => {
  const [broken, setBroken] = useState(false);
  const bgUrl = exp.scenes?.[0]?.background_url || '';
  const resolved = resolveThumbUrl(bgUrl);
  const initial = (displayI18n(exp.name, language) || '?')[0].toUpperCase();

  if (!resolved || broken) {
    return (
      <div className="w-16 h-10 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0"
        data-testid={`exp-thumb-placeholder-${exp.id}`}>
        <span className="text-sm font-bold text-slate-300">{initial}</span>
      </div>
    );
  }

  return (
    <img src={resolved} alt="" draggable={false}
      onError={() => setBroken(true)}
      className="w-16 h-10 rounded-lg object-cover border border-slate-200 flex-shrink-0"
      data-testid={`exp-thumb-${exp.id}`} />
  );
};

// ── Date range helpers ─────────────────────────
const toISOStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
const toISOEnd = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
const toInputDate = (d) => d.toISOString().slice(0, 10);

const DATE_PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: '7 Days', days: 7 },
  { key: '30d', label: '30 Days', days: 30 },
  { key: 'all', label: 'All Time', days: null },
];

const getPresetRange = (preset) => {
  if (preset === 'all') return { from: '', to: '' };
  const now = new Date();
  const end = toISOEnd(now);
  if (preset === 'today') return { from: toISOStart(now), to: end };
  const days = DATE_PRESETS.find(p => p.key === preset)?.days || 0;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { from: toISOStart(start), to: end };
};

// ── Analytics Panel ────────────────────────────
const AnalyticsPanel = ({ experience, onClose, language }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [activeRange, setActiveRange] = useState({ from: '', to: '' });

  const fetchAnalytics = useCallback(async (range) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.set('from_date', range.from);
      if (range.to) params.set('to_date', range.to);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await apiFetch(`/api/analytics/experience/${experience.id}${qs}`);
      setData(res);
    } catch {
      setData({ total_taps: 0, by_instance: {}, by_destination: {}, recent: [] });
    }
    setLoading(false);
  }, [experience.id]);

  useEffect(() => {
    const range = getPresetRange('all');
    setActiveRange(range);
    fetchAnalytics(range);
  }, [fetchAnalytics]);

  const selectPreset = (key) => {
    setPreset(key);
    const range = getPresetRange(key);
    setActiveRange(range);
    fetchAnalytics(range);
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    setPreset('custom');
    const range = {
      from: toISOStart(new Date(customFrom)),
      to: toISOEnd(new Date(customTo)),
    };
    setActiveRange(range);
    fetchAnalytics(range);
  };

  const expName = displayI18n(experience.name, language);
  const maxInstanceCount = data ? Math.max(1, ...Object.values(data.by_instance).map(v => v.count)) : 1;
  const maxDestCount = data ? Math.max(1, ...Object.values(data.by_destination)) : 1;

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto m-4" onClick={e => e.stopPropagation()} data-testid="analytics-panel">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-base font-bold text-slate-800">Tap Analytics</h2>
            <p className="text-xs text-slate-400 mt-0.5">{expName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="analytics-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Date range controls */}
          <div className="space-y-2">
            <div className="flex gap-1" data-testid="analytics-presets">
              {DATE_PRESETS.map(p => (
                <button key={p.key} type="button" onClick={() => selectPreset(p.key)} data-testid={`analytics-preset-${p.key}`}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                    preset === p.key ? 'bg-[#0077FF] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>{p.label}</button>
              ))}
              <button type="button" onClick={() => setPreset('custom')} data-testid="analytics-preset-custom"
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                  preset === 'custom' ? 'bg-[#0077FF] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}>Custom</button>
            </div>
            {preset === 'custom' && (
              <div className="flex items-center gap-2" data-testid="analytics-custom-range">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-1 focus:ring-[#0077FF]/30 outline-none"
                  data-testid="analytics-date-from" />
                <span className="text-xs text-slate-300">to</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-1 focus:ring-[#0077FF]/30 outline-none"
                  data-testid="analytics-date-to" />
                <button type="button" onClick={applyCustomRange} disabled={!customFrom || !customTo}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-[#0077FF] text-white disabled:opacity-40 hover:bg-[#0066DD] transition-colors"
                  data-testid="analytics-apply-custom">Apply</button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-400 text-sm">Loading analytics...</div>
          ) : data.total_taps === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-8 h-8 text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No taps recorded yet</p>
              <p className="text-xs text-slate-300 mt-1">Analytics will appear once guests start tapping cards</p>
            </div>
          ) : (<>
            {/* Total taps */}
            <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="analytics-total">
              <span className="text-sm text-slate-500">Total taps</span>
              <span className="text-2xl font-bold text-slate-800">{data.total_taps}</span>
            </div>

            {/* Taps by card instance */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">By Card Instance</h3>
              <div className="space-y-2">
                {Object.entries(data.by_instance)
                  .sort((a, b) => b[1].count - a[1].count)
                  .map(([iid, info]) => (
                    <div key={iid} className="flex items-center gap-3" data-testid={`analytics-instance-${iid}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{info.label || 'Unnamed'}</p>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0077FF] rounded-full transition-all" style={{ width: `${(info.count / maxInstanceCount) * 100}%` }} />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-slate-600 tabular-nums shrink-0">{info.count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Taps by destination */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">By Destination</h3>
              <div className="space-y-2">
                {Object.entries(data.by_destination)
                  .sort((a, b) => b[1] - a[1])
                  .map(([key, count]) => (
                    <div key={key} className="flex items-center gap-3" data-testid={`analytics-dest-${key}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 capitalize">{key.replace(/_/g, ' ')}</p>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(count / maxDestCount) * 100}%` }} />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-slate-600 tabular-nums shrink-0">{count}</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Recent taps */}
            <div>
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Recent Taps</h3>
              <div className="border border-slate-100 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="text-left px-3 py-2 text-slate-400 font-medium">Card</th>
                      <th className="text-left px-3 py-2 text-slate-400 font-medium">Destination</th>
                      <th className="text-right px-3 py-2 text-slate-400 font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.slice(0, 10).map((ev, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0">
                        <td className="px-3 py-2 text-slate-700 font-medium truncate max-w-[140px]">{ev.card_label || 'Card'}</td>
                        <td className="px-3 py-2 text-slate-500 capitalize">{(ev.destination_key || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 text-slate-400 text-right whitespace-nowrap">{formatTime(ev.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
};

const ExperienceList = ({ experiences, onEdit, onClone, onDelete, onPreview, onDemonstrate, onCreate, onReorder, onAnalytics, loading, t, language, canWrite, isSuperAdmin, deleting, confirmingDelete }) => (
  <div className="space-y-4" data-testid="experience-list">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{t('admin.experiences.title')}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{t('admin.experiences.subtitle')}</p>
      </div>
      {canWrite && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={onReorder} data-testid="reorder-experiences-btn"
            className="flex items-center gap-2 px-3.5 py-2.5 border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-medium rounded-xl transition-colors">
            <GripVertical className="w-4 h-4" /> Reorder
          </button>
          <button type="button" onClick={onCreate} data-testid="create-experience-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] text-white text-sm font-semibold rounded-xl transition-colors">
            <Plus className="w-4 h-4" /> {t('admin.experiences.new')}
          </button>
        </div>
      )}
    </div>

    {loading ? (
      <div className="text-center py-16 text-slate-400">{t('admin.common.loading')}</div>
    ) : experiences.length === 0 ? (
      <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
        <p className="text-slate-400 text-sm">{t('admin.common.noItems')}</p>
        {canWrite && (
          <button type="button" onClick={onCreate} className="mt-3 text-[#0077FF] text-sm font-medium hover:underline">
            {t('admin.experiences.new')}
          </button>
        )}
      </div>
    ) : (
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="experience-table">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left pl-4 pr-2 py-3 font-medium text-slate-500 w-[72px]"></th>
              <th className="text-left px-2 py-3 font-medium text-slate-500">{t('admin.experiences.name')}</th>
              <th className="text-left px-4 py-3 font-medium text-slate-500 hidden md:table-cell">{t('admin.experiences.brand')}</th>
              <th className="text-left px-4 py-3 font-medium text-slate-500 hidden md:table-cell">{t('admin.experiences.vertical')}</th>
              <th className="text-center px-4 py-3 font-medium text-slate-500">{t('admin.experiences.scenes')}</th>
              <th className="text-center px-4 py-3 font-medium text-slate-500">{t('admin.experiences.status')}</th>
              <th className="text-right px-4 py-3 font-medium text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {experiences.map(exp => {
              const cardCount = exp.scenes?.reduce((sum, s) => sum + (s.cards?.length || 0), 0) || 0;
              return (
                <tr key={exp.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`experience-row-${exp.id}`}>
                  <td className="pl-4 pr-2 py-2.5">
                    <ExperienceThumbnail exp={exp} language={language} />
                  </td>
                  <td className="px-2 py-2.5">
                    <p className="font-semibold text-slate-800">{displayI18n(exp.name, language)}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cardCount} card{cardCount !== 1 ? 's' : ''}</p>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 hidden md:table-cell">{exp.brand === 'yulontap' ? 'YULONTAP' : 'Tapelligence'}</td>
                  <td className="px-4 py-2.5 text-slate-600 capitalize hidden md:table-cell">{exp.vertical || '\u2014'}</td>
                  <td className="px-4 py-2.5 text-center text-slate-600">{exp.scenes?.length || 0}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${exp.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                      {exp.status === 'active' ? t('admin.common.active') : t('admin.common.draft')}
                    </span>
                    {exp.show_in_showroom === false && (
                      <span className="inline-flex ml-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700" data-testid={`exp-hidden-tag-${exp.id}`}>
                        Hidden
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => onPreview(exp)} title="Preview" className="p-1.5 text-slate-400 hover:text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors" data-testid={`preview-btn-${exp.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => onAnalytics(exp)} title="Analytics" className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors" data-testid={`analytics-btn-${exp.id}`}>
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      {isSuperAdmin && (
                        <button type="button" onClick={() => onDemonstrate(exp)} title="Demonstrate" data-testid={`demonstrate-btn-${exp.id}`}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors">
                          <Eye className="w-3.5 h-3.5" /> Demo
                        </button>
                      )}
                      {canWrite && <>
                        <button type="button" onClick={() => onEdit(exp)} title="Edit" className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-testid={`edit-btn-${exp.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => onClone(exp)} title="Clone" className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors" data-testid={`clone-btn-${exp.id}`}>
                          <Copy className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => onDelete(exp)} title={confirmingDelete === exp.id ? 'Tap again to confirm' : 'Delete'} disabled={deleting === exp.id}
                          className={`rounded-lg transition-all duration-200 ${
                            deleting === exp.id ? 'p-1.5 text-red-300'
                            : confirmingDelete === exp.id ? 'px-2.5 py-1.5 bg-red-500 text-white text-[10px] font-bold hover:bg-red-600'
                            : 'p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50'
                          }`} data-testid={`delete-btn-${exp.id}`}>
                          {deleting === exp.id ? (
                            <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                          ) : confirmingDelete === exp.id ? (
                            'Delete?'
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Scene Card Editor ──────────────────────────
const EXP_MODE_SENTINEL = '__exp_mode__';

const CardEditor = ({ card, cardIndex, onSaveCard, onRemove, capabilities, t, isActive, onSelect,
  availableModes = [], capabilityUrlOverrides = {}, platformDefaults = {}, modeLabels = {} }) => {
  // Local state — changes do NOT propagate to parent until Save Card
  const [pendingDests, setPendingDests] = useState(() => ({ ...(card.destinations || {}) }));
  const [pendingLabel, setPendingLabel] = useState(() => normI18n(card.label));
  const [pendingImage, setPendingImage] = useState(() => card.card_image_url || '');
  const [pendingCardType, setPendingCardType] = useState(() => card.card_type || '');
  const [pendingTriggerUrl, setPendingTriggerUrl] = useState(() => card.trigger_url || '');
  const [pendingAudioUrl, setPendingAudioUrl] = useState(() => card.audio_url || '');
  const [pendingAppearance, setPendingAppearance] = useState(() => card.card_appearance || '');
  const [showPicker, setShowPicker] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);

  const isAction = pendingCardType === 'action';

  // Dirty tracking — only for fields managed by this editor
  const savedDests = card.destinations || {};
  const savedLabel = normI18n(card.label);
  const savedImage = card.card_image_url || '';
  const isDirty = JSON.stringify(pendingDests) !== JSON.stringify(savedDests)
    || JSON.stringify(pendingLabel) !== JSON.stringify(savedLabel)
    || pendingImage !== savedImage
    || pendingCardType !== (card.card_type || '')
    || pendingTriggerUrl !== (card.trigger_url || '')
    || pendingAudioUrl !== (card.audio_url || '')
    || pendingAppearance !== (card.card_appearance || '');

  // Capability key derivation helper
  const capKey = (cap) => {
    const n = typeof cap.name === 'object' ? (cap.name.en || '') : (cap.name || '');
    return n.toLowerCase().replace(/\s+/g, '_');
  };

  // Add action from capability picker
  const addCapAction = (cap) => {
    const key = capKey(cap);
    if (!key || pendingDests[key] !== undefined) return;
    setPendingDests(prev => ({ ...prev, [key]: cap.default_destination_url || '' }));
    setShowPicker(false);
  };

  // Add Experience Mode action
  const addExpModeAction = (modeKey) => {
    if (pendingDests[modeKey] !== undefined) return;
    setPendingDests(prev => ({ ...prev, [modeKey]: EXP_MODE_SENTINEL }));
    setShowPicker(false);
  };

  // Resolve the effective URL for an experience mode
  const resolveExpModeUrl = (modeKey) => {
    if (capabilityUrlOverrides[modeKey]) return capabilityUrlOverrides[modeKey];
    if (platformDefaults[modeKey]) return platformDefaults[modeKey];
    return '';
  };

  // Get display label for a mode key
  const getModeDisplayLabel = (modeKey) => {
    const ml = modeLabels[modeKey];
    if (ml) {
      if (typeof ml === 'object') return ml.en || ml.fr || ml.es || modeKey;
      return ml;
    }
    return modeKey.replace(/[_-]/g, ' ');
  };

  const updateDest = (key, url) => setPendingDests(prev => ({ ...prev, [key]: url }));
  const removeDest = (key) => setPendingDests(prev => { const c = { ...prev }; delete c[key]; return c; });

  // Save card — commits local state to backend
  const handleSave = async () => {
    setSaveFeedback('saving');
    const payload = {
      ...card,
      label: pendingLabel,
      card_image_url: pendingImage,
      card_type: pendingCardType,
      trigger_url: pendingTriggerUrl,
      audio_url: pendingAudioUrl,
      card_appearance: pendingAppearance,
      destinations: isAction ? {} : pendingDests,
    };
    const success = await onSaveCard(cardIndex, payload);
    setSaveFeedback(success ? 'saved' : null);
    if (success) setTimeout(() => setSaveFeedback(null), 2500);
  };

  // Capabilities not yet added to this card
  const destKeys = Object.keys(pendingDests);
  const availableCaps = capabilities.filter(c => !destKeys.includes(capKey(c)));
  const capsByCategory = {};
  availableCaps.forEach(c => { const cat = c.category || 'other'; (capsByCategory[cat] = capsByCategory[cat] || []).push(c); });

  // Experience modes not yet added
  const availableExpModes = availableModes.filter(m => !destKeys.includes(m));

  const destEntries = Object.entries(pendingDests);

  return (
    <div onClick={onSelect}
      className={`border rounded-lg p-3 bg-white transition-colors cursor-pointer ${
        isActive ? 'border-[#0077FF] ring-1 ring-[#0077FF]/20' : 'border-slate-200 hover:border-slate-300'
      }`} data-testid={`card-editor-${cardIndex}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isAction ? <Zap className={`w-3.5 h-3.5 ${isActive ? 'text-red-500' : 'text-red-400'}`} />
            : <MapPin className={`w-3.5 h-3.5 ${isActive ? 'text-[#0077FF]' : 'text-slate-400'}`} />}
          <span className={`text-xs font-semibold ${isActive ? (isAction ? 'text-red-500' : 'text-[#0077FF]') : 'text-slate-600'}`}>
            {isAction ? 'Action Card' : `Card ${cardIndex + 1}`}
          </span>
          {isActive && <span className={`text-[9px] ${isAction ? 'bg-red-500/10 text-red-500' : 'bg-[#0077FF]/10 text-[#0077FF]'} font-medium px-1.5 py-0.5 rounded`}>Selected</span>}
          {isDirty && <span className="text-[9px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded" data-testid={`card-dirty-${cardIndex}`}>Unsaved changes</span>}
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-400 hover:text-red-600 p-1 rounded" data-testid={`remove-card-${cardIndex}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Card Type Toggle */}
      <div className="mb-2 flex items-center gap-2">
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</label>
        <select value={pendingCardType}
          onChange={e => setPendingCardType(e.target.value)}
          className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
          data-testid={`card-type-${cardIndex}`}>
          <option value="">Tap Card</option>
          <option value="action">Action Card</option>
        </select>
      </div>

      {/* Appearance */}
      <div className="mb-2 flex items-center gap-2">
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Appearance</label>
        <select value={pendingAppearance}
          onChange={e => setPendingAppearance(e.target.value)}
          className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
          data-testid={`card-appearance-${cardIndex}`}>
          <option value="">Standard card</option>
          <option value="image">Custom image</option>
          <option value="hotspot">Hotspot (dot only)</option>
          <option value="invisible">Invisible (tappable)</option>
        </select>
      </div>

      {/* Label */}
      <div className="mb-2">
        <LangInput value={pendingLabel} onChange={v => setPendingLabel(v)} placeholder={t('admin.experiences.cardLabel')} testId={`card-label-${cardIndex}`} />
      </div>

      {/* Image — hidden for hotspot/invisible */}
      {pendingAppearance !== 'hotspot' && pendingAppearance !== 'invisible' && (
        <div className="mb-2">
          <ImageUpload value={pendingImage} onChange={v => setPendingImage(v)}
            label={t('admin.experiences.cardImage') || 'Card Image'} testId={`card-image-${cardIndex}`} />
        </div>
      )}

      {/* Action card fields */}
      {isAction && (
        <div className="space-y-2 mb-2 border border-red-200/50 rounded-lg p-2.5 bg-red-50/30">
          <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider flex items-center gap-1">
            <Zap className="w-3 h-3" /> Action Settings
          </p>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 block mb-0.5">Trigger URL (POST)</label>
            <input value={pendingTriggerUrl} onChange={e => setPendingTriggerUrl(e.target.value)}
              placeholder="https://portal.yulontap.com/api/trigger/..."
              className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-600 focus:outline-none focus:ring-1 focus:ring-red-300/50"
              data-testid={`card-trigger-url-${cardIndex}`} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 block mb-0.5 flex items-center gap-1">
              <Volume2 className="w-3 h-3" /> Goal Horn (MP3)
            </label>
            {pendingAudioUrl ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-emerald-600 font-mono truncate max-w-[250px]">{pendingAudioUrl.split('/').pop()}</span>
                <button type="button" onClick={() => setPendingAudioUrl('')}
                  className="text-red-400 hover:text-red-600 p-0.5 rounded" data-testid={`card-audio-remove-${cardIndex}`}>
                  <X className="w-3 h-3" />
                </button>
                <audio controls src={pendingAudioUrl.startsWith('http') ? pendingAudioUrl : `${API_URL}${pendingAudioUrl}`}
                  className="h-6 max-w-[160px]" data-testid={`card-audio-preview-${cardIndex}`} />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg cursor-pointer transition-colors
                  ${audioUploading ? 'bg-slate-100 text-slate-400' : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200/50'}`}>
                  <Upload className="w-3 h-3" /> {audioUploading ? 'Uploading...' : 'Upload MP3'}
                  <input type="file" accept=".mp3,audio/mpeg" className="hidden"
                    disabled={audioUploading}
                    data-testid={`card-audio-upload-${cardIndex}`}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAudioUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await fetch(`${API_URL}/api/uploads/card-image`, {
                          method: 'POST', credentials: 'include', body: formData
                        });
                        if (!res.ok) throw new Error('Upload failed');
                        const data = await res.json();
                        setPendingAudioUrl(data.url);
                      } catch (err) { console.error('Audio upload failed', err); }
                      finally { setAudioUploading(false); e.target.value = ''; }
                    }} />
                </label>
                <span className="text-[10px] text-slate-400">Plays on tap</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions & Destinations — hidden for action cards */}
      {!isAction && (
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
          <Link2 className="w-3 h-3" /> Actions &amp; Destinations
        </p>
        {destEntries.length === 0 && (
          <p className="text-[10px] text-slate-400 italic" data-testid={`card-no-actions-${cardIndex}`}>No actions configured yet</p>
        )}
        {destEntries.map(([key, url]) => {
          const isExpMode = url === EXP_MODE_SENTINEL;
          const resolvedUrl = isExpMode ? resolveExpModeUrl(key) : '';
          const hasOverride = isExpMode && !!capabilityUrlOverrides[key];

          return (
            <div key={key} data-testid={`card-action-${cardIndex}-${key}`}>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-semibold w-24 shrink-0 truncate px-2 py-1.5 border rounded-lg capitalize ${
                  isExpMode ? 'text-violet-700 bg-violet-50 border-violet-200' : 'text-slate-600 bg-slate-50 border-slate-200'
                }`} title={key}>
                  {getModeDisplayLabel(key)}
                </span>
                {isExpMode ? (
                  <div className="flex-1 flex items-center gap-1.5 min-w-0">
                    <span className="text-[9px] font-semibold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded shrink-0" data-testid={`exp-mode-badge-${cardIndex}-${key}`}>
                      Experience Mode
                    </span>
                    {hasOverride ? (
                      <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">Override</span>
                    ) : (
                      <span className="text-[9px] font-medium text-slate-400 shrink-0">Platform Default</span>
                    )}
                    <span className="text-[10px] text-slate-500 truncate" title={resolvedUrl}>{resolvedUrl || '(no URL configured)'}</span>
                  </div>
                ) : (
                  <input value={url} onChange={e => updateDest(key, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }} placeholder="https://..."
                    className="flex-1 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
                    data-testid={`card-dest-${cardIndex}-${key}`} />
                )}
                <button type="button" onClick={(e) => { e.stopPropagation(); removeDest(key); }}
                  className="p-1 text-red-400 hover:text-red-600 rounded shrink-0" title="Remove action"
                  data-testid={`remove-action-${cardIndex}-${key}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}

        {/* + Add Action */}
        {!showPicker ? (
          <button type="button" onClick={(e) => { e.stopPropagation(); setShowPicker(true); }}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors"
            data-testid={`add-action-btn-${cardIndex}`}>
            <Plus className="w-3 h-3" /> Add Action
          </button>
        ) : (
          <div className="border border-[#0077FF]/30 bg-blue-50/40 rounded-lg p-2 space-y-2" data-testid={`capability-picker-${cardIndex}`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-[#0077FF]">Add an action</p>
              <button type="button" onClick={() => setShowPicker(false)} className="p-0.5 text-slate-400 hover:text-slate-600 rounded" data-testid={`close-picker-${cardIndex}`}>
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* Experience Mode actions */}
            {availableExpModes.length > 0 && (
              <div>
                <p className="text-[9px] font-bold text-violet-600 uppercase tracking-wider mb-1">Experience Modes</p>
                <div className="flex flex-wrap gap-1">
                  {availableExpModes.map(modeKey => (
                    <button key={modeKey} type="button" onClick={() => addExpModeAction(modeKey)}
                      className="px-2 py-1 text-[10px] font-medium bg-violet-50 border border-violet-200 text-violet-700 rounded-md hover:border-violet-400 hover:bg-violet-100 transition-colors"
                      data-testid={`pick-exp-mode-${cardIndex}-${modeKey}`}>
                      {getModeDisplayLabel(modeKey)}
                    </button>
                  ))}
                </div>
                <p className="text-[9px] text-slate-400 mt-1">Uses the Experience's configured URL (override or platform default)</p>
              </div>
            )}

            {/* Divider if both sections exist */}
            {availableExpModes.length > 0 && Object.keys(capsByCategory).length > 0 && (
              <div className="border-t border-slate-200 pt-1.5" />
            )}

            {/* Platform Capabilities */}
            {Object.keys(capsByCategory).length === 0 && availableExpModes.length === 0 ? (
              <p className="text-[10px] text-slate-400 italic">All actions already added</p>
            ) : Object.keys(capsByCategory).length > 0 && (
              <>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Platform Capabilities</p>
                {Object.entries(capsByCategory).map(([cat, caps]) => (
                  <div key={cat}>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{cat}</p>
                    <div className="flex flex-wrap gap-1">
                      {caps.map(cap => {
                        const name = typeof cap.name === 'object' ? (cap.name.en || cap.name.fr || '') : cap.name;
                        return (
                          <button key={cap.id} type="button" onClick={() => addCapAction(cap)}
                            className="px-2 py-1 text-[10px] font-medium bg-white border border-slate-200 rounded-md hover:border-[#0077FF] hover:text-[#0077FF] transition-colors"
                            data-testid={`pick-cap-${cardIndex}-${capKey(cap)}`}>
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
      )}

      {/* Save Card Button */}
      <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-end gap-2">
        {saveFeedback === 'saved' && (
          <span className="text-[10px] text-emerald-600 font-semibold" data-testid={`card-saved-${cardIndex}`}>Saved</span>
        )}
        <button type="button" onClick={(e) => { e.stopPropagation(); handleSave(); }}
          disabled={saveFeedback === 'saving'}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
            isDirty ? 'bg-[#0077FF] hover:bg-[#0066DD] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          } disabled:opacity-60`}
          data-testid={`save-card-btn-${cardIndex}`}>
          <Save className="w-3 h-3" />
          {saveFeedback === 'saving' ? 'Saving...' : 'Save Card'}
        </button>
      </div>
    </div>
  );
};

// ── Scene Editor ───────────────────────────────
const SceneEditor = ({ scene, sceneIndex, onChange, onRemove, capabilities, t, onSaveCard, availableModes, capabilityUrlOverrides, platformDefaults, modeLabels }) => {
  const [expanded, setExpanded] = useState(true);
  const [activeCardIdx, setActiveCardIdx] = useState(0);
  const [showCapPicker, setShowCapPicker] = useState(false);

  const updateField = (field, value) => onChange({ ...scene, [field]: value });
  const updateCard = (cardIdx, card) => { const cards = [...(scene.cards || [])]; cards[cardIdx] = card; onChange({ ...scene, cards }); };
  const addCard = (capability = null, cardType = '') => {
    const autoDestinations = {};
    if (cardType !== 'action') {
      (availableModes || []).forEach(modeKey => {
        autoDestinations[modeKey] = '__exp_mode__';
      });
    }
    const label = capability
      ? (typeof capability.name === 'object' ? { ...capability.name } : normI18n(capability.name))
      : cardType === 'action'
        ? { en: 'GOAL!', fr: 'BUT!', es: '¡GOL!' }
        : { en: 'New Card', fr: 'Nouvelle carte', es: 'Nueva tarjeta' };
    const imageUrl = capability?.default_image_url || '';
    const newCards = [...(scene.cards || []), {
      label,
      capability_id: capability?.id || '',
      card_image_url: imageUrl,
      position_x: 50, position_y: 50,
      card_scale: 1,
      destinations: autoDestinations,
      ...(cardType === 'action' ? { card_type: 'action', trigger_url: '', audio_url: '' } : {}),
    }];
    onChange({ ...scene, cards: newCards });
    setActiveCardIdx(newCards.length - 1);
    setShowCapPicker(false);
  };
  const removeCard = (cardIdx) => {
    onChange({ ...scene, cards: (scene.cards || []).filter((_, i) => i !== cardIdx) });
    setActiveCardIdx(Math.max(0, activeCardIdx >= cardIdx ? activeCardIdx - 1 : activeCardIdx));
  };
  const moveCard = (cardIdx, direction) => {
    const cards = [...(scene.cards || [])];
    const targetIdx = cardIdx + direction;
    if (targetIdx < 0 || targetIdx >= cards.length) return;
    [cards[cardIdx], cards[targetIdx]] = [cards[targetIdx], cards[cardIdx]];
    onChange({ ...scene, cards });
    setActiveCardIdx(targetIdx);
  };
  const updateCardPosition = (cardIdx, x, y) => {
    const cards = [...(scene.cards || [])];
    cards[cardIdx] = { ...cards[cardIdx], position_x: x, position_y: y };
    onChange({ ...scene, cards });
  };
  const updateTapArea = (cardIdx, w, h) => {
    const cards = [...(scene.cards || [])];
    cards[cardIdx] = { ...cards[cardIdx], tap_area_width: w, tap_area_height: h };
    onChange({ ...scene, cards });
  };

  // Sync all cards to the same visual size (common scale) preserving positions
  const syncCards = () => {
    if (cards.length < 2) return;
    const avgScale = cards.reduce((sum, c) => sum + (c.card_scale || 1), 0) / cards.length;
    const targetScale = Math.round(avgScale * 10) / 10 || 1.0;
    const syncedCards = cards.map(c => ({ ...c, card_scale: targetScale }));
    onChange({ ...scene, cards: syncedCards });
  };

  const cards = scene.cards || [];
  const bgUrl = scene.background_url || '';
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const resolvedBg = bgUrl ? (bgUrl.startsWith('http') ? bgUrl : `${API_URL}${bgUrl}`) : '';

  return (
    <div className="border border-slate-200 rounded-xl bg-slate-50/50" data-testid={`scene-editor-${sceneIndex}`}>
      <div onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-slate-50 rounded-t-xl transition-colors">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">{displayI18n(scene.name, 'en') || `Scene ${sceneIndex + 1}`}</span>
          <span className="text-[10px] text-slate-400">{cards.length} cards</span>
        </div>
        <div className="flex items-center gap-2">
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-400 hover:text-red-600 p-1 rounded cursor-pointer" data-testid={`remove-scene-${sceneIndex}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.experiences.sceneName')}</label>
              <LangInput value={scene.name} onChange={v => updateField('name', v)} testId={`scene-name-${sceneIndex}`} />
            </div>
            <div>
              <ImageUpload value={bgUrl} onChange={v => updateField('background_url', v)}
                label={t('admin.experiences.bgImage')} testId={`scene-bg-${sceneIndex}`} />
            </div>
          </div>

          {/* Visual Card Positioner */}
          {cards.length > 0 && (
            <div className="space-y-2 bg-gradient-to-b from-slate-100/80 to-white border border-slate-200 rounded-xl p-3" data-testid={`scene-positioner-${sceneIndex}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#0077FF]" /> Position &amp; Arrange Cards
                </p>
                <div className="flex items-center gap-2">
                  {cards.length > 1 && (
                    <button type="button" onClick={(e) => { e.preventDefault(); syncCards(); }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-[#0077FF] bg-[#0077FF]/10 hover:bg-[#0077FF]/20 border border-[#0077FF]/20 transition-colors"
                      data-testid={`sync-cards-${sceneIndex}`}>
                      <RefreshCw className="w-3 h-3" /> Sync Cards
                    </button>
                  )}
                  {cards.length > 1 && (
                    <p className="text-[9px] text-slate-400">Click a dot to select</p>
                  )}
                </div>
              </div>
              {cards.length > 1 && (
                <div className="flex gap-1 flex-wrap">
                  {cards.map((c, i) => (
                    <button type="button" key={i} onClick={() => setActiveCardIdx(i)}
                      data-testid={`scene-${sceneIndex}-card-tab-${i}`}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors ${
                        i === activeCardIdx ? 'bg-[#0077FF] text-white' : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                      }`}>
                      {displayI18n(c.label, 'en') || `Card ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              <CardPositioner
                bgUrl={resolvedBg}
                cards={cards}
                activeCardIndex={activeCardIdx}
                onUpdatePosition={updateCardPosition}
                onSelectCard={setActiveCardIdx}
                onUpdateTapArea={updateTapArea}
                placeholder="Upload a background image and add cards to position them"
              />
              {/* Fine-tune controls for selected card */}
              {cards[activeCardIdx] && (
                <div className="flex items-center gap-3 flex-wrap px-1">
                  <span className="text-[9px] font-semibold text-slate-400 uppercase">
                    {displayI18n(cards[activeCardIdx]?.label, 'en') || `Card ${activeCardIdx + 1}`}
                  </span>
                  <div className="flex items-center gap-1">
                    <label className="text-[9px] font-bold text-slate-400">X</label>
                    <input type="number" value={cards[activeCardIdx]?.position_x ?? 50}
                      onChange={e => updateCardPosition(activeCardIdx, parseFloat(e.target.value) || 0, cards[activeCardIdx]?.position_y ?? 50)}
                      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                      className="w-16 px-1.5 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] text-center"
                      data-testid={`scene-${sceneIndex}-pos-x-${activeCardIdx}`} />
                    <span className="text-[9px] text-slate-400">%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-[9px] font-bold text-slate-400">Y</label>
                    <input type="number" value={cards[activeCardIdx]?.position_y ?? 50}
                      onChange={e => updateCardPosition(activeCardIdx, cards[activeCardIdx]?.position_x ?? 50, parseFloat(e.target.value) || 0)}
                      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                      className="w-16 px-1.5 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] text-center"
                      data-testid={`scene-${sceneIndex}-pos-y-${activeCardIdx}`} />
                    <span className="text-[9px] text-slate-400">%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-[9px] font-bold text-slate-400">Scale</label>
                    <input type="range" min="0.15" max="4" step="0.05" value={cards[activeCardIdx]?.card_scale ?? 1}
                      onChange={e => { const cards2 = [...cards]; cards2[activeCardIdx] = { ...cards2[activeCardIdx], card_scale: parseFloat(e.target.value) }; onChange({ ...scene, cards: cards2 }); }}
                      className="w-20 h-1.5 accent-[#0077FF]" data-testid={`scene-${sceneIndex}-scale-${activeCardIdx}`} />
                    <span className="text-[10px] text-slate-500 font-mono w-7">{(cards[activeCardIdx]?.card_scale ?? 1).toFixed(1)}x</span>
                  </div>
                  {cards.length > 1 && (
                    <div className="flex items-center gap-0.5 ml-auto">
                      <span className="text-[9px] text-slate-400 mr-1">Layer</span>
                      <button type="button" onClick={() => moveCard(activeCardIdx, -1)} disabled={activeCardIdx === 0}
                        className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded" title="Move back (behind)"
                        data-testid={`scene-${sceneIndex}-move-back-${activeCardIdx}`}>
                        <MoveDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveCard(activeCardIdx, 1)} disabled={activeCardIdx === cards.length - 1}
                        className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 rounded" title="Move front (on top)"
                        data-testid={`scene-${sceneIndex}-move-front-${activeCardIdx}`}>
                        <MoveUp className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Tap Area Width/Height — for hotspot/invisible only */}
                  {(cards[activeCardIdx]?.card_appearance === 'hotspot' || cards[activeCardIdx]?.card_appearance === 'invisible') && (
                    <div className="flex items-center gap-3 w-full mt-1 pt-1 border-t border-slate-200/60">
                      <span className="text-[9px] font-semibold text-[#0077FF]/60 uppercase">Tap Area</span>
                      <div className="flex items-center gap-1">
                        <label className="text-[9px] font-bold text-slate-400">W</label>
                        <input type="number" min="5" max="100" step="1"
                          value={cards[activeCardIdx]?.tap_area_width || ''}
                          placeholder="0"
                          onChange={e => updateTapArea(activeCardIdx, parseFloat(e.target.value) || 0, cards[activeCardIdx]?.tap_area_height || 0)}
                          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                          className="w-14 px-1.5 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] text-center"
                          data-testid={`scene-${sceneIndex}-tap-w-${activeCardIdx}`} />
                        <span className="text-[9px] text-slate-400">%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="text-[9px] font-bold text-slate-400">H</label>
                        <input type="number" min="5" max="100" step="1"
                          value={cards[activeCardIdx]?.tap_area_height || ''}
                          placeholder="0"
                          onChange={e => updateTapArea(activeCardIdx, cards[activeCardIdx]?.tap_area_width || 0, parseFloat(e.target.value) || 0)}
                          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                          className="w-14 px-1.5 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] text-center"
                          data-testid={`scene-${sceneIndex}-tap-h-${activeCardIdx}`} />
                        <span className="text-[9px] text-slate-400">%</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Card Details */}
          <div className="space-y-2">
            {cards.map((card, ci) => (
              <CardEditor key={card.id || ci} card={card} cardIndex={ci}
                onSaveCard={onSaveCard} onRemove={() => removeCard(ci)} capabilities={capabilities} t={t}
                isActive={ci === activeCardIdx} onSelect={() => setActiveCardIdx(ci)}
                availableModes={availableModes} capabilityUrlOverrides={capabilityUrlOverrides}
                platformDefaults={platformDefaults} modeLabels={modeLabels} />
            ))}
            <div className="relative">
              <button type="button" onClick={() => setShowCapPicker(!showCapPicker)} data-testid={`add-card-btn-${sceneIndex}`}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" /> {t('admin.experiences.addCard')}
                <ChevronDown className={`w-3 h-3 transition-transform ${showCapPicker ? 'rotate-180' : ''}`} />
              </button>
              {showCapPicker && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowCapPicker(false)} />
                  <div className="absolute left-0 bottom-full mb-1 z-40 w-72 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg p-2 space-y-1" data-testid={`cap-picker-${sceneIndex}`}>
                    <button type="button" onClick={() => addCard(null)} data-testid={`add-blank-card-${sceneIndex}`}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                      <Plus className="w-4 h-4 text-slate-400" /> Blank Card
                    </button>
                    <button type="button" onClick={() => addCard(null, 'action')} data-testid={`add-action-card-${sceneIndex}`}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Zap className="w-4 h-4 text-red-400" /> Action Card
                    </button>
                    {capabilities.length > 0 && <>
                      <div className="border-t border-slate-100 my-1" />
                      <p className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">From Capability</p>
                      {capabilities.map(cap => {
                        const name = typeof cap.name === 'object' ? (cap.name.en || cap.name.fr || '') : (cap.name || '');
                        const instanceCount = cards.filter(c => c.capability_id === cap.id).length;
                        return (
                          <button key={cap.id} type="button" onClick={() => addCard(cap)} data-testid={`add-cap-card-${sceneIndex}-${cap.id}`}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-blue-50 hover:text-[#0077FF] rounded-lg transition-colors">
                            {cap.default_image_url ? (
                              <img src={cap.default_image_url.startsWith('http') ? cap.default_image_url : `${API_URL}${cap.default_image_url}`} alt=""
                                className="w-6 h-6 rounded object-contain bg-white border border-slate-100 shrink-0" />
                            ) : (
                              <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center shrink-0">
                                <Image className="w-3 h-3 text-slate-300" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="truncate">{name}</span>
                              {instanceCount > 0 && <span className="text-[10px] text-emerald-500 font-normal ml-1">{instanceCount} in scene</span>}
                            </div>
                          </button>
                        );
                      })}
                    </>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Scene Template Picker ──────────────────────
const SceneTemplatePicker = ({ onSelect, t }) => {
  const [open, setOpen] = useState(false);

  const pick = (template) => {
    onSelect(template);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} data-testid="add-scene-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#0077FF] hover:bg-blue-50 rounded-lg transition-colors">
        <Plus className="w-3.5 h-3.5" /> {t('admin.experiences.addScene')}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-2 space-y-1" data-testid="scene-template-picker">
            <button type="button" onClick={() => pick(null)} data-testid="scene-tpl-blank"
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
              <FileText className="w-4 h-4 text-slate-400" />
              <span>Start from scratch</span>
            </button>
            <div className="border-t border-slate-100 my-1" />
            <p className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Templates</p>
            {SCENE_TEMPLATES.map(tpl => {
              const Icon = tpl.icon;
              return (
                <button key={tpl.key} type="button" onClick={() => pick(tpl)} data-testid={`scene-tpl-${tpl.key}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-blue-50 hover:text-[#0077FF] rounded-lg transition-colors">
                  <Icon className="w-4 h-4" />
                  <div>
                    <span>{tpl.label}</span>
                    <span className="block text-[10px] text-slate-400 font-normal">{tpl.cards.length} cards</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ── Experience Editor ──────────────────────────
const ExperienceEditor = ({ experience, onSave, onCancel, saving, capabilities, t }) => {
  const [form, setForm] = useState(() => ({
    name: { en: '', fr: '', es: '' },
    slug: '',
    brand: 'yulontap',
    vertical: '',
    status: 'draft',
    show_in_showroom: true,
    available_modes: ['standard'],
    mode_labels: { standard: { en: 'Standard', fr: 'Standard', es: 'Estandar' } },
    capability_url_overrides: {},
    social_media_config: [],
    scenes: [],
    ...experience,
    // Normalize name if loading from legacy string
    ...(experience?.name && typeof experience.name === 'string' ? { name: { en: experience.name, fr: '', es: '' } } : {}),
  }));

  // Platform default URLs per mode key (fetched from backend)
  const [platformDefaults, setPlatformDefaults] = useState({});
  useEffect(() => {
    fetch(`${API_URL}/api/capabilities/mode-defaults`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { defaults: {} })
      .then(d => setPlatformDefaults(d.defaults || {}))
      .catch(() => {});
  }, []);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleMode = (modeKey) => {
    setForm(prev => {
      const modes = prev.available_modes.includes(modeKey)
        ? prev.available_modes.filter(m => m !== modeKey)
        : [...prev.available_modes, modeKey];
      const labels = { ...prev.mode_labels };
      if (!modes.includes(modeKey)) delete labels[modeKey];
      else if (!labels[modeKey]) labels[modeKey] = normI18n(DEFAULT_MODES.find(m => m.key === modeKey)?.label || modeKey);
      return { ...prev, available_modes: modes, mode_labels: labels };
    });
  };

  const updateModeLabel = (modeKey, value) => {
    setForm(prev => ({ ...prev, mode_labels: { ...prev.mode_labels, [modeKey]: value } }));
  };

  const updateScene = (idx, scene) => setForm(prev => { const s = [...prev.scenes]; s[idx] = scene; return { ...prev, scenes: s }; });
  const addScene = (template = null) => {
    const modeDestinations = {};
    (form.available_modes || []).forEach(m => { modeDestinations[m] = '__exp_mode__'; });

    if (template) {
      const cards = template.cards.map(c => ({
        label: { ...c.label },
        position_x: c.position_x,
        position_y: c.position_y,
        card_scale: 1,
        destinations: { ...modeDestinations },
      }));
      setForm(prev => ({
        ...prev,
        scenes: [...prev.scenes, { name: { ...template.sceneName }, background_url: '', cards }],
      }));
    } else {
      setForm(prev => ({
        ...prev,
        scenes: [...prev.scenes, { name: { en: `Scene ${prev.scenes.length + 1}`, fr: `Scene ${prev.scenes.length + 1}`, es: `Escena ${prev.scenes.length + 1}` }, background_url: '', cards: [] }],
      }));
    }
  };
  const removeScene = (idx) => setForm(prev => ({ ...prev, scenes: prev.scenes.filter((_, i) => i !== idx) }));

  // Save a single card inline (commits card changes to form + saves to backend)
  const saveCardInScene = async (sceneIdx, cardIdx, updatedCard) => {
    const updatedScenes = [...form.scenes];
    const cards = [...updatedScenes[sceneIdx].cards];
    cards[cardIdx] = updatedCard;
    updatedScenes[sceneIdx] = { ...updatedScenes[sceneIdx], cards };
    const updatedForm = { ...form, scenes: updatedScenes };
    setForm(updatedForm);
    try {
      const result = await onSave(updatedForm, true);
      return !!result;
    } catch { return false; }
  };

  return (
    <div className="space-y-5" data-testid="experience-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="editor-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-slate-900">{experience?.id ? t('admin.experiences.edit') : t('admin.experiences.new')}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-xl transition-colors" data-testid="cancel-btn">
            {t('admin.common.cancel')}
          </button>
          <button type="button" onClick={() => onSave(form)} disabled={saving || !(typeof form.name === 'string' ? form.name : form.name?.en)} data-testid="save-experience-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-slate-300 text-white text-sm font-semibold rounded-xl transition-colors">
            <Save className="w-4 h-4" /> {saving ? t('admin.common.saving') : t('admin.common.save')}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h2 className="text-sm font-bold text-slate-700">{t('admin.experiences.basicInfo')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.experiences.name')}</label>
            <LangInput value={form.name} onChange={v => updateField('name', v)} testId="exp-name" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.experiences.slug')}</label>
            <input value={form.slug} onChange={e => updateField('slug', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }} placeholder="auto-generated"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30 mt-6"
              data-testid="exp-slug-input" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.experiences.brand')}</label>
            <select value={form.brand} onChange={e => updateField('brand', e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30 mt-6"
              data-testid="exp-brand-select">
              {BRANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 block">{t('admin.experiences.vertical')}</label>
            <input value={form.vertical} onChange={e => updateField('vertical', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }} placeholder="restaurants, hotels..."
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30 mt-6"
              data-testid="exp-vertical-input" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t('admin.experiences.status')}</label>
          <button type="button" onClick={() => updateField('status', form.status === 'active' ? 'draft' : 'active')}
            className={`relative w-10 h-5 rounded-full transition-colors ${form.status === 'active' ? 'bg-emerald-500' : 'bg-slate-300'}`}
            data-testid="exp-status-toggle">
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.status === 'active' ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-xs text-slate-500">{form.status === 'active' ? t('admin.common.active') : t('admin.common.draft')}</span>
          <div className="h-4 w-px bg-slate-200 mx-2" />
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Show in showroom</label>
          <button type="button" role="switch" aria-checked={form.show_in_showroom !== false}
            onClick={() => updateField('show_in_showroom', form.show_in_showroom === false)}
            className={`relative w-10 h-5 rounded-full transition-colors ${form.show_in_showroom !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
            data-testid="exp-show-in-showroom-toggle">
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.show_in_showroom !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-xs text-slate-500" data-testid="exp-show-in-showroom-label">{form.show_in_showroom !== false ? 'Shown' : 'Hidden'}</span>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-bold text-slate-700">{t('admin.experiences.modes')}</h2>
        <div className="space-y-3">
          {DEFAULT_MODES.map(mode => {
            const isEnabled = form.available_modes.includes(mode.key);
            const overrideUrl = form.capability_url_overrides?.[mode.key] || '';
            const defaultUrl = platformDefaults[mode.key] || '';
            const hasOverride = !!overrideUrl;

            return (
              <div key={mode.key} className={`rounded-lg border p-3 transition-colors ${isEnabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/50'}`}
                data-testid={`mode-row-${mode.key}`}>
                <div className="flex items-start gap-3">
                  <button type="button" onClick={() => toggleMode(mode.key)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 mt-1
                      ${isEnabled ? 'bg-[#0077FF] border-[#0077FF]' : 'border-slate-300 hover:border-slate-400'}`}
                    data-testid={`mode-check-${mode.key}`}>
                    {isEnabled && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <LangInput
                      value={form.mode_labels[mode.key] || normI18n(mode.label)}
                      onChange={v => updateModeLabel(mode.key, v)}
                      placeholder={mode.label}
                      testId={`mode-label-${mode.key}`}
                      className={`max-w-xs ${!isEnabled ? 'opacity-40 pointer-events-none' : ''}`}
                    />
                    {/* URL Override field — only when mode is enabled */}
                    {isEnabled && (
                      <div className="mt-2" data-testid={`url-override-${mode.key}`}>
                        <div className="flex items-center gap-2">
                          <Link2 className="w-3 h-3 text-slate-400 shrink-0" />
                          <label className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">URL Override</label>
                          {hasOverride ? (
                            <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-testid={`override-badge-${mode.key}`}>Custom for this Experience</span>
                          ) : defaultUrl ? (
                            <span className="text-[9px] font-medium text-slate-400" data-testid={`inherited-badge-${mode.key}`}>Inherited from Platform</span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            value={overrideUrl}
                            onChange={e => {
                              const val = e.target.value;
                              setForm(prev => ({
                                ...prev,
                                capability_url_overrides: {
                                  ...prev.capability_url_overrides,
                                  [mode.key]: val
                                }
                              }));
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
                            placeholder={defaultUrl || 'https://...'}
                            className={`flex-1 px-2.5 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30 ${
                              hasOverride
                                ? 'bg-amber-50/50 border-amber-200 text-slate-800'
                                : 'bg-slate-50 border-slate-200 text-slate-500'
                            }`}
                            data-testid={`url-override-input-${mode.key}`}
                          />
                          {hasOverride && (
                            <button
                              type="button"
                              onClick={() => {
                                setForm(prev => {
                                  const next = { ...prev.capability_url_overrides };
                                  delete next[mode.key];
                                  return { ...prev, capability_url_overrides: next };
                                });
                              }}
                              className="shrink-0 text-[9px] font-semibold text-slate-400 hover:text-[#0077FF] px-2 py-1.5 border border-slate-200 hover:border-[#0077FF]/30 rounded-lg transition-colors"
                              data-testid={`reset-override-${mode.key}`}
                            >
                              Use Platform Default
                            </button>
                          )}
                        </div>
                        {!hasOverride && defaultUrl && (
                          <p className="text-[10px] text-slate-400 mt-1 truncate" title={defaultUrl} data-testid={`default-url-display-${mode.key}`}>
                            {defaultUrl}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Social Media Destinations ── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-700">Social Media Destinations</h2>
            <p className="text-[10px] text-slate-400 mt-0.5">Map social media platforms to cards. Each platform appears in the demo only on its assigned cards.</p>
          </div>
          <button type="button" onClick={() => {
            setForm(prev => ({
              ...prev,
              social_media_config: [...(prev.social_media_config || []), { label: '', show_on: 'all', card_ids: [], external_url: '' }]
            }));
          }} className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-[#0077FF] bg-[#0077FF]/5 rounded-lg hover:bg-[#0077FF]/10 transition-colors"
            data-testid="add-social-media-btn">
            <Plus className="w-3 h-3" /> Add Platform
          </button>
        </div>
        {(form.social_media_config || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic py-2">No social media destinations configured. The social media selector will be hidden in the demo.</p>
        ) : (
          <div className="space-y-4">
            {(form.social_media_config || []).map((sm, idx) => {
              const allCards = (form.scenes || []).flatMap((scene, si) =>
                (scene.cards || []).map((card, ci) => {
                  const cardLabel = typeof card.label === 'string' ? card.label : (card.label?.en || `Card ${ci + 1}`);
                  const sceneName = typeof scene.name === 'string' ? scene.name : (scene.name?.en || `Scene ${si + 1}`);
                  return { id: card.id, label: cardLabel, sceneName };
                })
              );
              // Backward compat: migrate old card_id → card_ids + show_on
              const showOn = sm.show_on || (sm.card_id ? 'selected' : 'all');
              const cardIds = sm.card_ids && sm.card_ids.length > 0
                ? sm.card_ids
                : (sm.card_id ? [sm.card_id] : []);
              const checkedCount = showOn === 'selected' ? cardIds.length : allCards.length;
              return (
                <div key={idx} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50" data-testid={`social-media-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <input value={sm.label} placeholder="e.g., Facebook"
                      onChange={e => {
                        setForm(prev => {
                          const updated = [...(prev.social_media_config || [])];
                          updated[idx] = { ...updated[idx], label: e.target.value };
                          return { ...prev, social_media_config: updated };
                        });
                      }}
                      className="w-[140px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
                      data-testid={`social-media-label-${idx}`} />
                    <select value={showOn}
                      onChange={e => {
                        setForm(prev => {
                          const updated = [...(prev.social_media_config || [])];
                          updated[idx] = { ...updated[idx], show_on: e.target.value, card_id: '' };
                          if (e.target.value === 'all') {
                            updated[idx].card_ids = [];
                          }
                          return { ...prev, social_media_config: updated };
                        });
                      }}
                      className="w-[180px] px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30"
                      data-testid={`social-media-show-on-${idx}`}>
                      <option value="all">Show on: All cards</option>
                      <option value="selected">Show on: Selected cards</option>
                    </select>
                    <span className="text-[10px] text-slate-400">
                      {checkedCount} card{checkedCount !== 1 ? 's' : ''}
                    </span>
                    <div className="flex-1" />
                    <button type="button" onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        social_media_config: (prev.social_media_config || []).filter((_, i) => i !== idx)
                      }));
                    }} className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                      data-testid={`remove-social-media-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Card checklist — visible when "Selected cards" */}
                  {showOn === 'selected' && (
                    <div className="mt-2 ml-1 space-y-1" data-testid={`social-media-card-checklist-${idx}`}>
                      {allCards.length === 0 ? (
                        <p className="text-[10px] text-slate-400 italic">No cards in any scene yet.</p>
                      ) : allCards.map(c => {
                        const checked = cardIds.includes(c.id);
                        return (
                          <label key={c.id} className="flex items-center gap-2 cursor-pointer group py-0.5"
                            data-testid={`social-media-card-check-${idx}-${c.id}`}>
                            <input type="checkbox" checked={checked}
                              onChange={e => {
                                setForm(prev => {
                                  const updated = [...(prev.social_media_config || [])];
                                  const current = updated[idx].card_ids || (updated[idx].card_id ? [updated[idx].card_id] : []);
                                  const next = e.target.checked
                                    ? [...new Set([...current, c.id])]
                                    : current.filter(id => id !== c.id);
                                  updated[idx] = { ...updated[idx], card_ids: next, card_id: '' };
                                  return { ...prev, social_media_config: updated };
                                });
                              }}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-[#0077FF] focus:ring-[#0077FF]/30" />
                            <span className={`text-xs ${checked ? 'text-slate-700 font-medium' : 'text-slate-500'} group-hover:text-slate-700`}>
                              {c.sceneName} — {c.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* External URL */}
                  <div className="mt-2 ml-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap w-[72px]">External URL</span>
                      <input value={sm.external_url || ''}
                        placeholder="https://www.facebook.com/your-page"
                        onChange={e => {
                          setForm(prev => {
                            const updated = [...(prev.social_media_config || [])];
                            updated[idx] = { ...updated[idx], external_url: e.target.value };
                            return { ...prev, social_media_config: updated };
                          });
                        }}
                        className="flex-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-mono text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#0077FF]/30 focus:border-[#0077FF]/30 placeholder:text-slate-300"
                        data-testid={`social-media-external-url-${idx}`} />
                    </div>
                    {sm.external_url && (
                      <div className="flex items-center gap-1.5 mt-1 text-[10px] text-emerald-600/80">
                        <span className="text-slate-300">&rarr;</span>
                        <span className="font-mono truncate max-w-[500px]" title={sm.external_url}>{sm.external_url}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">{t('admin.experiences.scenes')}</h2>
          <SceneTemplatePicker onSelect={(tpl) => addScene(tpl)} t={t} />
        </div>
        {form.scenes.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
            <p className="text-slate-400 text-sm">{t('admin.experiences.noScenes')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {form.scenes.map((scene, i) => (
              <SceneEditor key={scene.id || i} scene={scene} sceneIndex={i}
                onChange={(s) => updateScene(i, s)} onRemove={() => removeScene(i)} capabilities={capabilities} t={t}
                onSaveCard={(cardIdx, card) => saveCardInScene(i, cardIdx, card)}
                availableModes={form.available_modes} capabilityUrlOverrides={form.capability_url_overrides}
                platformDefaults={platformDefaults} modeLabels={form.mode_labels} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WIZARD_STORAGE_KEY = 'tapelligence_wizard_state';

const saveWizardState = (editing, useGuide) => {
  try {
    if (editing !== null) {
      sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify({ editing, useGuide }));
    } else {
      sessionStorage.removeItem(WIZARD_STORAGE_KEY);
    }
  } catch (_) { /* ignore */ }
};

const loadWizardState = () => {
  try {
    const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* ignore */ }
  return null;
};

const clearWizardState = () => {
  try { sessionStorage.removeItem(WIZARD_STORAGE_KEY); } catch (_) { /* ignore */ }
};

// ── Main Page ──────────────────────────────────
const ExperienceManagerPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const auth = useAdminAuth();
  const isSuperAdmin = auth.role === 'super_admin';
  const canWrite = isSuperAdmin || auth.access_type === 'full_access';
  const [experiences, setExperiences] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(() => {
    const saved = loadWizardState();
    return saved ? saved.editing : null;
  });
  const [useGuide, setUseGuide] = useState(() => {
    const saved = loadWizardState();
    return saved ? saved.useGuide : false;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [analyticsExp, setAnalyticsExp] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [expData, capData] = await Promise.all([apiFetch('/api/experiences'), apiFetch('/api/capabilities')]);
      setExperiences(expData.items || []);
      setCapabilities(capData.items || []);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Persist wizard state to sessionStorage
  useEffect(() => {
    saveWizardState(editing, useGuide);
  }, [editing, useGuide]);

  // Save handler that returns the saved object (needed by GuidedSetup for preview)
  const handleSave = async (form, returnResult = false) => {
    setSaving(true); setError('');
    try {
      let result;
      if (form.id) {
        result = await apiFetch(`/api/experiences/${form.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      } else {
        result = await apiFetch('/api/experiences', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      }
      if (returnResult) { setSaving(false); return result; }
      clearWizardState();
      setEditing(null); setUseGuide(false); fetchData();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const [deleting, setDeleting] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [showReorder, setShowReorder] = useState(false);

  const handleReorder = async (orderedIds) => {
    try {
      await apiFetch('/api/experiences/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordered_ids: orderedIds }),
      });
      setShowReorder(false);
      fetchData();
    } catch (err) { setError(err.message); }
  };

  const handleClone = async (exp) => {
    try { await apiFetch(`/api/experiences/${exp.id}/clone`, { method: 'POST' }); fetchData(); } catch (err) { setError(err.message); }
  };

  const handleDelete = async (exp) => {
    if (confirmingDelete !== exp.id) {
      setConfirmingDelete(exp.id);
      setTimeout(() => setConfirmingDelete(prev => prev === exp.id ? null : prev), 4000);
      return;
    }
    setConfirmingDelete(null);
    setDeleting(exp.id);
    try {
      await apiFetch(`/api/experiences/${exp.id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handlePreview = (exp) => {
    window.open(`/experience/admin-preview/${exp.id}?lang=${language}`, '_blank');
  };

  const handleDemonstrate = (exp) => {
    window.open(`/experience/admin-preview/${exp.id}?lang=${language}&mode=demonstrate`, '_blank');
  };

  // Create new → open GuidedSetup by default
  const handleCreate = () => {
    setEditing({});
    setUseGuide(true);
  };

  // Skip GuidedSetup → Manual Editor, carrying over form data
  const handleSkipGuide = (formData) => {
    setEditing(formData && Object.keys(formData).length > 0 ? formData : editing);
    setUseGuide(false);
  };

  const handleCancelEdit = () => {
    clearWizardState();
    setEditing(null);
    setUseGuide(false);
  };

  // Determine which view to render
  const renderContent = () => {
    if (editing === null) {
      return (
        <>
          <ExperienceList experiences={experiences} loading={loading} onEdit={(exp) => { setEditing(exp); setUseGuide(false); }} onClone={handleClone} onDelete={handleDelete} onPreview={handlePreview} onDemonstrate={handleDemonstrate} onAnalytics={setAnalyticsExp} onCreate={handleCreate} onReorder={() => setShowReorder(true)} t={t} language={language} canWrite={canWrite} isSuperAdmin={isSuperAdmin} deleting={deleting} confirmingDelete={confirmingDelete} />
          {analyticsExp && <AnalyticsPanel experience={analyticsExp} onClose={() => setAnalyticsExp(null)} language={language} />}
        </>
      );
    }
    if (useGuide) {
      return (
        <GuidedSetup
          experience={editing}
          capabilities={capabilities}
          onSave={handleSave}
          onCancel={handleCancelEdit}
          onSkip={handleSkipGuide}
          saving={saving}
          t={t}
          language={language}
        />
      );
    }
    return (
      <ExperienceEditor experience={editing} onSave={handleSave} onCancel={handleCancelEdit} saving={saving} capabilities={capabilities} t={t} />
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="experience-manager-page">
      <AdminNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="error-message">
            {error}
            <button type="button" onClick={() => setError('')} className="float-right text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        )}
        {showReorder && (
          <ReorderModal experiences={experiences} onClose={() => setShowReorder(false)} onSave={handleReorder} language={language} />
        )}
        {renderContent()}
      </div>
    </div>
  );
};

export default ExperienceManagerPage;
